/**
 * Persisted collapsed/expanded state for the app sidebar.
 *
 * The whole sidebar can fold to a narrow icon-only rail (the panel-toggle
 * pattern). The choice persists across reloads in localStorage so the user
 * keeps their preferred layout. Lives under `components/layout` because both
 * `AppShellLayout` (which narrows the grid column) and `AppSidebar` (which
 * renders the rail) read it.
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'af.sidebar.collapsed';

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** `[collapsed, toggle]`. Toggle flips the rail and writes localStorage. */
export function useSidebarCollapsed(): readonly [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // Ignore write failures (private mode, quota). State still works
      // for the session; only the persistence is lost.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((value) => !value), []);

  return [collapsed, toggle] as const;
}
